/** @file Import session, upload step: runs the import from `choose` or a failed draft, resumable. */
import type { ImportCore, ImportDraft, ImportProgress } from './importTypes';
import { runImport } from './runImport';

/** The draft after a progress report: what is uploaded, and the project once a new one exists. */
const advanced = (draft: ImportDraft, p: ImportProgress): ImportDraft => ({ ...draft, uploaded: p.uploaded, target: { kind: 'project', projectId: p.projectId } });

/** The draft a start may run: the choosing step's, or a failed upload's (retry). */
function startable(core: ImportCore): ImportDraft | null {
  const step = core.store.getState();
  if (step.kind === 'choose') return step.draft;
  return step.kind === 'failed' ? step.draft : null;
}

/** Uploads the checked layers; a failure keeps what was uploaded so `start` again resumes. */
export function startImport(core: ImportCore): () => Promise<void> {
  return async () => {
    const first = startable(core);
    if (first === null || !first.choices.some((c) => c.checked)) return;
    const ticket = core.ticket.n;
    let draft = first;
    const total = first.choices.filter((c) => c.checked).length;
    core.store.setState({ kind: 'uploading', draft, done: first.uploaded.size, total }, true);
    try {
      const outcome = await runImport(draft, core.deps.ports, (p) => {
        draft = advanced(draft, p);
        if (ticket === core.ticket.n) core.store.setState({ kind: 'uploading', draft, done: p.uploaded.size, total: p.total }, true);
      });
      await core.deps.afterImport(outcome);
      if (ticket === core.ticket.n) core.store.setState({ kind: 'done', draft, outcome }, true);
    } catch (thrown) {
      if (ticket === core.ticket.n) core.store.setState({ kind: 'failed', draft, thrown }, true);
    } finally {
      // The session was disposed while this run was in flight: nothing will close its PSD worker any more.
      if (ticket !== core.ticket.n && draft.source.kind === 'psd') draft.source.session.close();
    }
  };
}
